import JSZip from 'jszip';
import { validateScenario, type Scenario } from '@branchworks/shared';
import { RUNTIME_HTML } from './runtimeHtml.generated';
import { SCORM_API_JS, ADAPTER_JS, buildManifest } from './scormAssets';

/**
 * Assemble a SCORM 1.2 package for a scenario, entirely client-side.
 *
 * The package is the inlined runtime HTML plus four siblings: the scenario
 * baked into a global (Slice 5's embedded entry point), the SCORM API wrapper,
 * the completion-only adapter, and the manifest. The three helper scripts are
 * injected into the runtime HTML's <head> as classic scripts so they run before
 * the runtime's deferred module — the API and scenario global are ready by the
 * time the player mounts and starts emitting events.
 *
 * Export refuses on validation errors (a broken scenario shouldn't ship to an
 * LMS); warnings don't block. Returns the zip bytes, so the same function works
 * in the browser and in tests under Node.
 */

const FILES = {
  index: 'index.html',
  api: 'scorm-api.js',
  scenario: 'scenario.js',
  adapter: 'branchworks-adapter.js',
  manifest: 'imsmanifest.xml',
};

function slugify(s: string): string {
  return (s || 'scenario')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'scenario';
}

function injectScripts(html: string): string {
  const tags =
    `<script src="${FILES.api}"></script>` +
    `<script src="${FILES.scenario}"></script>` +
    `<script src="${FILES.adapter}"></script>`;
  if (html.includes('</head>')) return html.replace('</head>', `${tags}</head>`);
  // Fallback: prepend into <body> if no </head> (shouldn't happen with our build).
  return html.replace('<body>', `<body>${tags}`);
}

export type AssembleResult =
  | { ok: true; bytes: Uint8Array; filename: string }
  | { ok: false; errors: string[] };

export async function assembleScormZip(scenario: Scenario): Promise<AssembleResult> {
  const validation = validateScenario(scenario);
  if (validation.errors.length > 0) {
    return {
      ok: false,
      errors: validation.errors.map((e) => {
        const label = e.path?.nodeLabel || e.path?.nodeId;
        return label ? `${label}: ${e.message}` : e.message;
      }),
    };
  }

  const slug = slugify(scenario.title);
  const id = `bw-${slug}`;
  const scenarioJs = `window.__BRANCHWORKS_SCENARIO__ = ${JSON.stringify(scenario)};`;
  const html = injectScripts(RUNTIME_HTML);
  const manifest = buildManifest({
    title: scenario.title,
    id,
    files: [FILES.index, FILES.api, FILES.scenario, FILES.adapter],
  });

  const zip = new JSZip();
  // Manifest and all content sit at the zip ROOT — no wrapping folder, or many
  // LMSs reject the package.
  zip.file(FILES.manifest, manifest);
  zip.file(FILES.index, html);
  zip.file(FILES.api, SCORM_API_JS);
  zip.file(FILES.scenario, scenarioJs);
  zip.file(FILES.adapter, ADAPTER_JS);

  const bytes = await zip.generateAsync({
    type: 'uint8array',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
  return { ok: true, bytes, filename: `${slug}-scorm.zip` };
}

/** Browser-only: assemble and trigger a download. Returns errors if blocked. */
export async function downloadScormPackage(scenario: Scenario): Promise<{ ok: true } | { ok: false; errors: string[] }> {
  const result = await assembleScormZip(scenario);
  if (!result.ok) return result;
  const blob = new Blob([result.bytes as unknown as BlobPart], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = result.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return { ok: true };
}
